/**
 * @voltxampere/web — URL hash share (Sprint 21 C4)
 *
 * Encode the current Circuit into a URL hash fragment so the
 * sharer can DM a link and the recipient sees the same circuit
 * on first paint. D-21-5 closure: gzip + base64url + 8 KB
 * warning + ".vxa indir" CTA fallback (Şefim approved).
 *
 * Encoding pipeline:
 *
 *   1. JSON.stringify(circuit)
 *   2. gzip via CompressionStream (~30-60% reduction)
 *   3. base64url encode (URL-safe alphabet, no `=` padding)
 *   4. Prepend `#share=`
 *
 * Decoding pipeline:
 *
 *   1. Parse `#share=...` from `window.location.hash`
 *   2. base64url decode
 *   3. Gunzip via DecompressionStream
 *   4. JSON.parse
 *   5. migrateToLatest (forward-compat with older shared links)
 *
 * Hash size limit: a conservative 8 KB safety net keeps the URL
 * short enough for common chat clients (Slack truncates ~3.5 KB,
 * Twitter ~4 KB). Oversized circuits raise `ShareTooLargeError`
 * so the share button can fall back to "Yerine .vxa indir".
 *
 * Browser baseline: matches Sprint 21 C2 .vxa codec
 * (Chrome 80+, Firefox 113+, Safari 16.4+).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { migrateToLatest, type Circuit } from "@voltxampere/schema";

export const SHARE_HASH_PREFIX = "#share=" as const;
export const SHARE_HASH_PARAM = "share" as const;

/** Threshold in bytes — encoded hash strings longer than this
 *  trigger a fallback path. 8 KB conservative — common chat
 *  clients truncate at 3.5-4 KB but the longer mime-friendly
 *  bound covers most editor-generated circuits. */
export const SHARE_HASH_MAX_BYTES = 8 * 1024;

export class ShareTooLargeError extends Error {
  override readonly name = "ShareTooLargeError";
  constructor(
    readonly bytes: number,
    readonly limit: number,
  ) {
    super(`Share payload ${String(bytes)} bytes exceeds ${String(limit)} byte limit`);
  }
}

export class ShareDecodeError extends Error {
  override readonly name = "ShareDecodeError";
  override readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    if (cause !== undefined) this.cause = cause;
  }
}

export interface EncodeShareOpts {
  /** Override the byte limit (test fixture support). */
  readonly limitBytes?: number;
}

/**
 * Encode a Circuit into a `#share=...` hash. Returns the
 * `share=<base64url>` body without the leading `#` so callers
 * can compose it into a longer URL.
 *
 * Throws `ShareTooLargeError` when the encoded payload exceeds
 * `SHARE_HASH_MAX_BYTES` (Şefim D-21-5: 8 KB).
 */
export async function encodeShareHash(
  circuit: Circuit,
  opts: EncodeShareOpts = {},
): Promise<string> {
  const limit = opts.limitBytes ?? SHARE_HASH_MAX_BYTES;
  const json = JSON.stringify(circuit);
  const compressed = await gzipString(json);
  const b64u = bytesToBase64Url(new Uint8Array(compressed));
  const hashBody = `${SHARE_HASH_PARAM}=${b64u}`;
  if (hashBody.length > limit) {
    throw new ShareTooLargeError(hashBody.length, limit);
  }
  return hashBody;
}

/**
 * Build a fully-qualified shareable URL from the current page's
 * origin + path + hash body. Tests pass a custom `baseUrl` so
 * they don't depend on `window.location`.
 */
export function buildShareUrl(hashBody: string, baseUrl?: string): string {
  const base =
    baseUrl ??
    (typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname}` : "");
  return `${base}#${hashBody}`;
}

/**
 * Decode a `#share=` hash. Accepts either the bare body
 * (`share=...`) or the leading `#share=...` form.
 */
export async function decodeShareHash(hash: string): Promise<Circuit> {
  let payload = hash;
  if (payload.startsWith("#")) payload = payload.slice(1);
  if (!payload.startsWith(`${SHARE_HASH_PARAM}=`)) {
    throw new ShareDecodeError(`decodeShareHash: hash missing "${SHARE_HASH_PARAM}=" prefix`);
  }
  const b64u = payload.slice(`${SHARE_HASH_PARAM}=`.length);
  let compressed: Uint8Array;
  try {
    compressed = base64UrlToBytes(b64u);
  } catch (err) {
    throw new ShareDecodeError("decodeShareHash: invalid base64url payload", err);
  }
  let json: string;
  try {
    json = await ungzipBytes(compressed);
  } catch (err) {
    throw new ShareDecodeError("decodeShareHash: gzip decompression failed", err);
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (err) {
    throw new ShareDecodeError("decodeShareHash: payload is not valid JSON", err);
  }
  try {
    return migrateToLatest(raw);
  } catch (err) {
    throw new ShareDecodeError("decodeShareHash: schema migration failed", err);
  }
}

/**
 * Inspect a hash string and return the embedded Circuit if one is
 * present, or `null` otherwise. Boot-time helper that tolerates
 * arbitrary `window.location.hash` content (e.g. `#simulator`
 * pane router prefix).
 */
export async function maybeParseShareHash(hash: string): Promise<Circuit | null> {
  if (!hash) return null;
  let body = hash;
  if (body.startsWith("#")) body = body.slice(1);
  // Multiple hash fragments separated by `&` are tolerated.
  for (const part of body.split("&")) {
    if (part.startsWith(`${SHARE_HASH_PARAM}=`)) {
      return await decodeShareHash(part);
    }
  }
  return null;
}

// ────────────────────────────────────────────────────────────────
// gzip / base64url helpers
// ────────────────────────────────────────────────────────────────

async function gzipString(input: string): Promise<ArrayBuffer> {
  if (typeof CompressionStream === "undefined") {
    throw new Error("gzipString: CompressionStream API not available");
  }
  const inputBytes = new TextEncoder().encode(input);
  const stream = new Blob([inputBytes]).stream().pipeThrough(new CompressionStream("gzip"));
  return await new Response(stream).arrayBuffer();
}

async function ungzipBytes(input: Uint8Array): Promise<string> {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("ungzipBytes: DecompressionStream API not available");
  }
  // Sprint 27 Lane 2 — Uint8Array is `Uint8Array<ArrayBufferLike>` under
  // TS 5.6+, but BlobPart now insists on `ArrayBuffer` (rejects
  // SharedArrayBuffer). Cast at the boundary; runtime Uint8Array is
  // always a valid BlobPart.
  const stream = new Blob([input as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream("gzip"));
  return await new Response(stream).text();
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  const b64 =
    typeof btoa !== "undefined" ? btoa(binary) : Buffer.from(binary, "binary").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

function base64UrlToBytes(b64u: string): Uint8Array {
  // Re-pad to a multiple of 4.
  const remainder = b64u.length % 4;
  const padded = remainder === 0 ? b64u : b64u + "=".repeat(4 - remainder);
  const b64 = padded.replace(/-/g, "+").replace(/_/g, "/");
  const binary =
    typeof atob !== "undefined" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
